import { useState, useEffect } from "react";

// react router
import { useNavigate, useLocation } from "react-router-dom";

// redux
import { useSelector } from "react-redux";

// assets
import Illustration from "../assets/images/login-signup-illustration.png";
import ClientSideDarkLogo from "../../../assets/clientsidedark.png";
import ClientSideLightLogo from "../../../assets/client-side-light.png";

// pages
import LoginTab from "../components/login/LoginTab";
import ResetPassword from "../components/ResetPassword";
import SignupTab from "../components/signup/SignupTab";
import CodeConfirmation from "../components/CodeConfirmation";
import EmailVerification from "../components/EmailVerification/EmailVerification";
import RegisterConfirm from "../components/RegisterConfirm/RegisterConfirm";
import PasswordRecoveryTab from "../components/passwordRecovery/PasswordRecoveryTab";
import EmailVerificationRequired from "../components/EmailVerificationRequired/EmailVerificationRequired";

function UserTabsNavigation() {
  const navigate = useNavigate()
  const location = useLocation()
  // console.log("EEE", location.pathname)
  const searchParams = new URLSearchParams(location.search)

  const theme = useSelector((state) => state.theme.value)
  const assets = useSelector(state => state.exchangeAssets.value)
  const exchangeName = useSelector(state => state.exchangeCustomization.value.name)

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const [isTablet, setIsTablet] = useState(window.innerWidth > 768 && window.innerWidth <= 1024)
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480)

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth
      setIsMobile(width <= 768)
      setIsTablet(width > 768 && width <= 1024)
      setIsSmallMobile(width <= 480)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Get route-specific content for left side
  const getLeftSideContent = () => {
    switch (location.pathname) {
      case "/login":
        return {
          title: "It's time to level up & conquer greatness!",
          description: "Welcome back! Sign in to access your crypto trading platform and manage your portfolio with confidence."
        }
      case "/signup":
        return {
          title: "Welcome to the club!",
          description: "You're just a few techs away from unlocking greatness. Join thousands of traders who trust WealthTech for secure crypto trading."
        }
      case "/recover-password":
        return {
          title: "Forgot Your Password?",
          description: "No worries! Enter your email address and we'll send you a link to reset your password.",
          subtitle: "The reset link will be valid for 24 hours.",
          extraContent: (
            <div style={{
              marginTop: '30px',
              padding: isSmallMobile ? '15px' : '20px',
              background: theme === 'light' 
                ? 'rgba(33, 37, 41, 0.1)' 
                : 'rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '15px',
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={theme === 'light' ? '#212529' : 'white'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '2px' }}>
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
                <div>
                  <div style={{ 
                    color: theme === 'light' ? '#212529' : 'white', 
                    fontWeight: 600, 
                    marginBottom: '8px', 
                    fontSize: isSmallMobile ? '14px' : '15px' 
                  }}>
                    What happens next?
                  </div>
                  <ul style={{ 
                    color: theme === 'light' 
                      ? 'rgba(33, 37, 41, 0.8)' 
                      : 'rgba(255, 255, 255, 0.8)', 
                    fontSize: isSmallMobile ? '13px' : '14px',
                    lineHeight: 1.6,
                    margin: 0,
                    paddingLeft: '20px',
                  }}>
                    <li style={{ marginBottom: '5px' }}>Check your email inbox</li>
                    <li style={{ marginBottom: '5px' }}>Click the reset link we send</li>
                    <li>Create a new password</li>
                  </ul>
                </div>
              </div>
            </div>
          )
        }
      case "/email-verification-required":
        return {
          title: "Verify Your Email",
          description: "We've sent a verification link to your email address. Please check your inbox and click the link to verify your account and continue."
        }
      default:
        return {
          title: "It's time to level up & conquer greatness!",
          description: "Welcome back! Sign in to access your crypto trading platform and manage your portfolio with confidence."
        }
    }
  }

  const leftContent = getLeftSideContent()

  return (
    <div style={{
      height: '100vh',
      width: '100vw',
      background: theme === 'light' ? '#f8f9fa' : '#001E28',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '20px 15px' : '40px 20px',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        maxWidth: '1200px',
        width: '100%',
        height: isMobile ? 'calc(100vh - 40px)' : 'calc(100vh - 80px)',
        background: 'transparent',
        borderRadius: isMobile ? '15px' : '20px',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
      }}>
        {/* Left Section - Branding */}
        {!isMobile && (
          <div style={{
            flex: 1,
            background: theme === 'light' 
              ? 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
              : 'linear-gradient(135deg, #009999 0%, #007a7a 100%)',
            padding: isMobile ? '40px 30px' : isTablet ? '50px 40px' : '60px 50px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            borderRadius: isMobile ? '15px 15px 0 0' : '20px 0 0 20px',
          }}>


            <div style={{
              position: 'relative',
              zIndex: 1,
            }}>
              {/* Main logo/brand */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: isMobile ? '25px' : '40px',
              }}>
                <img
                  src={theme === 'light' ? ClientSideLightLogo : ClientSideDarkLogo}
                  alt="WealthTech Logo"
                  width={isMobile ? "200" : "280"}
                  height={isMobile ? "auto" : "auto"}
                  style={{ objectFit: 'contain' }}
                />
              </div>

              <h1 style={{
                fontSize: isMobile ? '28px' : '48px',
                color: theme === 'light' ? '#212529' : 'white',
                marginBottom: isMobile ? '15px' : '20px',
                lineHeight: 1.2,
                fontWeight: 700,
              }}>
                {leftContent.title}
              </h1>
              <p style={{
                fontSize: isMobile ? '15px' : '18px',
                color: theme === 'light' ? 'rgba(33, 37, 41, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                lineHeight: 1.6,
                marginBottom: leftContent.subtitle ? '15px' : '15px',
              }}>
                {leftContent.description}
              </p>
              {leftContent.subtitle && (
                <p style={{
                  fontSize: isMobile ? '15px' : '18px',
                  color: theme === 'light' ? 'rgba(33, 37, 41, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                  lineHeight: 1.6,
                  marginBottom: '15px',
                }}>
                  {leftContent.subtitle}
                </p>
              )}
              {leftContent.extraContent}
            </div>
          </div>
        )}

        {/* Right Section - Form Container */}
        <div style={{
          flex: 1,
          padding: isMobile ? '40px 30px' : '60px 50px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          background: theme === 'light' 
            ? (isMobile ? '#ffffff' : '#ffffff')
            : (isMobile ? '#001E28' : 'rgb(1, 29, 38)'),
          overflowY: 'auto',
          borderRadius: isMobile ? '0 0 15px 15px' : '0 20px 20px 0',
          maxHeight: '100%',
        }}>
          {/* TABS DISPLAY */}
          {location.pathname === "/login" && <LoginTab />}
          {location.pathname === "/signup" && <SignupTab />}
          {location.pathname === "/recover-password" && <PasswordRecoveryTab />}
          {location.pathname === "/confirmation-code" && <CodeConfirmation />}
          {location.pathname === "/confirm-register" && <RegisterConfirm />}
          {location.pathname === "/accounts/password_reset" && <ResetPassword />}
          {location.pathname === "/email-verification-required" && (
            <EmailVerificationRequired />
          )}
          {location.pathname === "/accounts/confirmation" &&
            searchParams.get("confirmation_token") && (
              <EmailVerification />
            )}
        </div>
      </div>
    </div>
  )
}

export default UserTabsNavigation
